import React from 'react';
import { DotmSquare11 } from '~/components/ui/dotm-square-11';
import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import styles from './ArtifactCard.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ArtifactCardProps {
  /** 13/17 semibold at `--cv15-text`. The artifact's name — `Retention` in all six artboards. */
  title: string;
  /** 11/14 regular at `--cv15-text-hint`. Provenance, not a subtitle: `artifact 03 · just now`. */
  meta: string;
  /**
   * Goes inside the 44x25 plate. Omit and you get the flat white plate the design ships — Paper
   * draws every one of the six cards empty, so an empty preview is the resting picture, never a
   * missing one.
   */
  thumbnail?: React.ReactNode;
  /**
   * Opens the artifact. With a handler the whole card is a `<button>`; without one it is the
   * static row Paper drew. See the note on the component.
   */
  onOpen?: () => void;
  /** Replaces the opening chevron with the shared directory-style loader while the artifact is being written. */
  busy?: boolean;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The 56px row an assistant turn ends on: preview plate, name, provenance, chevron.
 *
 * Deliberately **not** the same component as `TaskCard`, though the two are nine pixels apart and
 * share a border, a radius and an 11px gap. This one is 56 tall with a 44x25 thumbnail and a
 * chevron-right; that one is 52 with a 17px glyph and a status word, and has a dashed variant this
 * one never wears. Folding them into one box with a `variant` prop would average four numbers the
 * file states independently in nine artboards — the surest way to have both drift.
 *
 * The chevron points right because the artifact opens *elsewhere*: this row is a door, not a
 * disclosure. `TaskCard`'s external variant makes the same promise with an arrow-up-right, and
 * neither ever points down.
 */
export function ArtifactCard({
  title,
  meta,
  thumbnail,
  onOpen,
  busy = false,
  theme: themeProp,
  className,
}: ArtifactCardProps) {
  const theme = useV15Theme(themeProp);
  const rootClass = cn(
    chatThemeClass(theme),
    styles.card,
    onOpen && styles.interactive,
    className,
  );

  const body = (
    <>
      {/* The plate is painted whether or not anything is in it — an empty preview is the picture
          all six artboards draw, so there is no "no thumbnail" branch here, only an empty one. */}
      <span className={styles.thumb}>{thumbnail}</span>
      <span className={styles.stack}>
        <span className={styles.title}>{title}</span>
        <span className={styles.meta}>{meta}</span>
      </span>
      {/* A real element rather than `margin-left: auto`, because Paper draws one and because the
          text stack has to be allowed to shrink before this does. */}
      <span className={styles.spacer} />
      {/* The busy mark is the directory's own working mark. It occupies the same trailing slot as
          the chevron, so the card does not reflow when the artifact settles. */}
      {busy ? (
        <span className={styles.busy} aria-label="Writing artifact">
          <DotmSquare11 size={15} dotSize={2} />
        </span>
      ) : null}
      {/* Chrome, not identity and not lifecycle: it says "this opens", which is a fact about the
          card rather than about the artifact. That is why it comes through the atom's third axis
          and not its `tool`/`state` ones — §1.7 specifies it at the card level, trailing
          `chevron-right 17 --cv15-text-muted` after the spacer.

          `tone="inherit"` rather than `tone="muted"`, because the resting muted is only half of it:
          `.chevron` lifts to subtle under the card's pointer, and a glyph that paints its own
          colour could not be lifted by the rule that does it. */}
      {!busy ? (
        <span className={styles.chevron}>
          <ActivityGlyph chrome="chevron-right" size={17} tone="inherit" theme={theme} />
        </span>
      ) : null}
    </>
  );

  // A `<button>` only when there is something behind it. BUILD-CONTRACT §6 says anything clickable
  // is a real button; the converse matters just as much — a card with no handler must not sit in
  // the tab order promising a door that opens onto nothing. Same split as `AttachmentCard`'s dot.
  if (onOpen) {
    return (
      <button type="button" className={rootClass} onClick={onOpen}>
        {body}
      </button>
    );
  }

  return <div className={rootClass}>{body}</div>;
}
